import { observer } from 'mobx-react';
import { ReactNode } from 'react';
import { T } from '../../../../Translations';
import BigCheckmarkCircleIcon from '../../../Cutesies/BigCheckmarkCircleIcon';
import Modal from '../../../Modal';

interface Props {
  heading: ReactNode;
  name: string;
  licenseCount: number;
  collectionCount: number;
  onClose: () => void;
  // The action buttons, stacked below the summary. Callers supply their own
  // (Add offers three, Edit two) so the shared chrome stays identical.
  children: ReactNode;
}

/**
 * Shared post-save confirmation modal for the Add Group and Edit Group
 * workflows: checkmark, heading, group name, a licenses/collections summary,
 * and a caller-provided stack of action buttons. Keeps the two success modals
 * visually identical. The fixed width stops a longer heading (e.g. "Group
 * Successfully Edited") from auto-widening one modal relative to the other.
 */
function GroupSuccessModal({
  heading,
  name,
  licenseCount,
  collectionCount,
  onClose,
  children,
}: Props) {
  return (
    <Modal close={onClose} title="" modalWidth="w-[480px]">
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <BigCheckmarkCircleIcon width="80" height="80" />
        </div>

        <h2 className="text-xl mb-0.5" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
          {heading}
        </h2>

        <p className="text-muted-foreground mb-4" style={{ fontSize: 'var(--text-sm)' }}>
          {name}
        </p>

        <div className="bg-muted rounded-lg px-3.5 py-2.5 mb-4">
          <div className="flex justify-between py-1.5" style={{ fontSize: 'var(--text-sm)' }}>
            <span className="text-muted-foreground">
              <T kind="Licenses" />
            </span>
            <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
              {licenseCount} <T kind="associated" />
            </span>
          </div>
          <div className="flex justify-between py-1.5" style={{ fontSize: 'var(--text-sm)' }}>
            <span className="text-muted-foreground">
              <T kind="Learning Collections" />
            </span>
            <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
              {collectionCount} <T kind="associated" />
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">{children}</div>
      </div>
    </Modal>
  );
}

export default observer(GroupSuccessModal);
